import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Icon from '../../../components/icons';
import Button from '../../../components/elements/button';
import { useBillComApi } from '../../../store/api/hooks/billComApiHook';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import { useDialog } from '../../../store/api/hooks/dialogHook';

type ConnectorsFormValueType = {
    billComClientID?: string;
    billComClientSecret?: string;
    billComOrgID?: string;
    billComAPIKey?: string;
    billComLastSyncDate?: string;
};

const BillComConnector: React.FC = () => {
    const billComApi: any = useBillComApi();
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch, getValues, trigger } = useForm<ConnectorsFormValueType>
        ({
            defaultValues: {}
        });
    const [showApiKey, setShowApiKey] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [showOrgID, setShowOrgID] = useState(false);
    const { showLoader, hideLoader } = useAppLoader();
    const { confirm } = useDialog();
    const [config, setConfig] = useState<ConnectorsFormValueType | null>(null);

    const formReset = () => {
        reset({
            billComClientID: '',
            billComClientSecret: '',
            billComOrgID: '',
            billComAPIKey: '',
        });
        setShowApiKey(false);
        setShowClientSecret(false);
        setShowOrgID(false);
    };

    const onSubmit: SubmitHandler<ConnectorsFormValueType> = formData => {
        showLoader();
        const payload = {
            billComClientID: formData.billComClientID,
            billComClientSecret: formData.billComClientSecret,
            billComOrgID: formData.billComOrgID,
            billComAPIKey: formData.billComAPIKey,
        };

        confirm({
            type: 'warn',
            title: 'Save Bill.com Connection',
            message: 'Do you want to save the connection?',
            onConfirm: () => {
                // Remove environment from save payload as it's not needed for save
                const savePayload = {
                    billComClientID: formData.billComClientID,
                    billComClientSecret: formData.billComClientSecret,
                    billComOrgID: formData.billComOrgID,
                    billComAPIKey: formData.billComAPIKey,
                };
                billComApi.saveConnection(savePayload, (resp: any) => {
                    if (resp.successful) {
                        hideLoader();
                        toast.success(resp?.message);
                        formReset();
                        getBillComConfig();
                    } else {
                        hideLoader();
                        toast.error(resp.message || "Connection Failed");
                    }
                }, (error: any) => {
                    hideLoader();
                    toast.error(error);
                });
            },
            onCancel: () => {
                hideLoader();
            }
        });
    };

    const callTestConnection = async () => {
        const isValid = await trigger();
        if (!isValid) {
            return;
        }
        showLoader();
        const formData = getValues();
        const payload = {
            billComClientID: formData.billComClientID,
            billComClientSecret: formData.billComClientSecret,
            billComOrgID: formData.billComOrgID,
            billComAPIKey: formData.billComAPIKey,
        };
        billComApi.checkTestConnection(
            payload,
            (resp: any) => {
                console.log("response =>", resp);
                if (resp.successful) {
                    hideLoader();
                    toast.success(resp?.message);
                } else {
                    hideLoader();
                    toast.error(resp.message || "Connection Failed");
                }
            },
            (error: any) => {
                console.log("error =>", error);
                hideLoader();
                toast.error(error);
            }
        );
    };

    const getBillComConfig = () => {
        const payload: any = {};
        billComApi.getBillComConfig(
            payload,
            (resp: any) => {
                console.log("response =>", resp);
                const data = resp;

                setConfig(data);

                setValue("billComClientID", data.billComClientID || '');
                setValue("billComClientSecret", data.billComClientSecret || '');
                setValue("billComOrgID", data.billComOrgID || '');
                setValue("billComAPIKey", data.billComAPIKey || '');
                // billComLastSyncDate is read-only, just store in config
            },
            (error: any) => {
                console.log('error =>', error);
                toast.error(error);
            }
        );
    };

    useEffect(() => {
        getBillComConfig();
    }, []);

    return (
        <Layout>
            <Header title="Bill.com Connector" back="/settings" />
            <div className="flex justify-center items-center min-h-[calc(100vh-100px)] bg-gray-100 px-4 py-8">
                <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                        Provide Following details to connect with your Bill.com account
                    </h2>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                        <div>
                            <Controller
                                name="billComClientID"
                                control={control}
                                rules={{ required: "Client ID Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Bill.com Client ID"
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        required
                                        error={errors.billComClientID?.message}
                                    />
                                )}
                            />
                        </div>
                        <div className="relative w-full">
                            <Controller
                                name="billComClientSecret"
                                control={control}
                                rules={{ required: "Client Secret Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Bill.com Client Secret"
                                        type={showClientSecret ? 'text' : 'text'} // keep as text to show asterisks
                                        value={
                                            showClientSecret
                                                ? watch("billComClientSecret") || ''
                                                : '*'.repeat((watch("billComClientSecret") || '').length)
                                        }
                                        onChange={(e) => {
                                            const realValue = e.target.value;
                                            setValue("billComClientSecret", realValue);
                                        }}
                                        required
                                        error={errors.billComClientSecret?.message}
                                    />
                                )}
                            />
                            <div
                                onClick={() => setShowClientSecret(prev => !prev)}
                                className="absolute top-1/2 right-2 translate-y-[10%] cursor-pointer text-gray-500 select-none"
                            >
                                <Icon
                                    name={showClientSecret ? "eye-closed" : "eye"} size={1.2}
                                />
                            </div>
                        </div>
                        <div className="relative w-full">
                            <Controller
                                name="billComOrgID"
                                control={control}
                                rules={{ required: "Organization ID Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Bill.com Organization ID"
                                        type={showOrgID ? 'text' : 'text'} // keep as text to show asterisks
                                        value={
                                            showOrgID
                                                ? watch("billComOrgID") || ''
                                                : '*'.repeat((watch("billComOrgID") || '').length)
                                        }
                                        onChange={(e) => {
                                            const realValue = e.target.value;
                                            setValue("billComOrgID", realValue);
                                        }}
                                        required
                                        error={errors.billComOrgID?.message}
                                    />
                                )}
                            />
                            <div
                                onClick={() => setShowOrgID(prev => !prev)}
                                className="absolute top-1/2 right-2 translate-y-[10%] cursor-pointer text-gray-500 select-none"
                            >
                                <Icon
                                    name={showOrgID ? "eye-closed" : "eye"} size={1.2}
                                />
                            </div>
                        </div>
                        <div className="relative w-full">
                            <Controller
                                name="billComAPIKey"
                                control={control}
                                rules={{ required: "API Key Is Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="API Key"
                                        type={showApiKey ? 'text' : 'text'} // keep as text to show asterisks
                                        value={
                                            showApiKey
                                                ? watch("billComAPIKey") || ''
                                                : '*'.repeat((watch("billComAPIKey") || '').length)
                                        }
                                        onChange={(e) => {
                                            const realValue = e.target.value;
                                            setValue("billComAPIKey", realValue);
                                        }}
                                        required
                                        error={errors.billComAPIKey?.message}
                                    />
                                )}
                            />
                            <div
                                onClick={() => setShowApiKey(prev => !prev)}
                                className="absolute top-1/2 right-2 translate-y-[10%] cursor-pointer text-gray-500 select-none"
                            >
                                <Icon
                                    name={showApiKey ? "eye-closed" : "eye"} size={1.2}
                                />
                            </div>
                        </div>

                        {config?.billComLastSyncDate && (
                            <div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Last Sync Date
                                    </label>
                                    <input
                                        type="text"
                                        value={new Date(config.billComLastSyncDate).toLocaleString()}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between pt-4">
                            <Button type="button" theme='dark' onClick={() => { callTestConnection(); }}>
                                Test Connection
                            </Button>
                            <Button type="button" theme='secondary' onClick={() => { formReset(); }}>
                                Reset
                            </Button>
                            <Button type="submit">
                                Save
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default BillComConnector;